import React, { useEffect, useState } from 'react';
import '../event/EventList.css';
import EventList from '../event/EventList';
import UnregisteredPlayersSection from '../event/UnregisteredPlayersSection';
import { useParams } from 'react-router-dom';
import api from '../wrapper/API';
import CreateForm from '../form/CreateForm';
import { useSuccessBar } from '../context/SuccessBarContext';
import CustomTogglebox from '../input/CustomTogglebox';
import Popup from '../popup/Popup';
import { usePopup } from '../context/PopupContext';
import './Event.css';
import './Registrations.css';
import CustomComboBox from '../input/CustomComboBox';
import Icon from '../home/Icon';
import { isAdmin } from '../../util/auth';
import { eventRules } from '../../util/eventRules';
import { mailSlotLabel } from '../../util/mailSlots';
import { available, taken } from '../../util/addOns';
import { useTour } from '../context/TourContext';
import { EVENT_TOUR_KEY, eventTourSteps } from '../tour/tours/eventTour';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import { CandidateGroupMeta } from './MatchPill';
import Textfield from '../input/Textfield';
import { scoreClass } from '../../util/matchScore';
import { loadEvent } from '../../offline/offlineApi';
import { useOffline } from '../context/OfflineContext';
import { subscribeSync } from '../../offline/syncEngine';
import EventAddOnInputs from '../event/EventAddOnInputs';
import { describeExhaustedAddOns, eventSignupAddOns, toQuantityInputs, withReductions } from '../event/addOnMessages';

// Admin fügt einen Spieler direkt zu einem Spieltag hinzu.
// Flow:
//   1. 1–4 Felder (Nachname, Vorname, Email, Telefon) eingeben, Backend normalisiert (ContactNormalizer).
//   2. Backend liefert entweder:
//        - 'Created'          → Spieler angelegt/gefunden und hinzugefügt.
//        - 'WouldBeWaitlisted'→ Spieltag voll (waitlistReason 'event') oder ein Zusatz mit „Ausgebucht → Warteliste“
//                               ausgebucht ('addOn', + waitlistAddOns) — Admin bestätigt die (Zusatz-)Warteliste
//                               oder erzwingt Confirmed.
//        - 'NeedsSelection'   → Fuzzy-Kandidaten, Admin wählt im Picker-Popup.
//        - 'NoMatch'          → Keine Treffer, Admin bestätigt Neuanlage im confirmcreatenew-Popup.
//   3. Admin-Wahl → zweiter Call mit ReferencePlayerId ODER CreateNew=true, ggf. mit ForceConfirm/ParticipantType/
//      ForceAddOnWaitlist. Zusätze ohne Warteliste werden bei Knappheit gekürzt (reducedAddOns → Erfolgsmeldung).
function Event() {
  document.documentElement.style.setProperty('--defaultMarginContainer', '10px')
  const [posts, setPosts] = useState([]);
  const { eventId } = useParams();
  const { eventDate } = useParams();
  const { openPopup, closePopup } = usePopup();
  const { isTourCompleted, startTour, tourReady } = useTour();
  useEffect(() => {
    if (!tourReady || posts.length === 0) return;
    if (!isTourCompleted(EVENT_TOUR_KEY)) {
      startTour(EVENT_TOUR_KEY, eventTourSteps);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourReady, posts.length]);
  const [candidates, setCandidates] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [maxGroup, setMaxGroup] = useState(0);
  // Zwischenablage der Formularwerte während der Admin zwischen Picker/Confirm-Popups navigiert.
  const [pendingRequest, setPendingRequest] = useState(null);
  const [waitlistReason, setWaitlistReason] = useState(null);
  // Ausgebuchte Zusätze (AddOnCapacityInfo) bei waitlistReason 'addOn' — für den Popup-Text.
  const [waitlistAddOns, setWaitlistAddOns] = useState([]);

  // Event-Metadaten (maxPlayers, Zusätze) für die Stats-Bar in EventList.
  const [eventDetails, setEventDetails] = useState(null);
  // Anmelde-Zusätze des Prozesses (Prozess-Reihenfolge) — ohne Prozess bzw. solange die Details laden keine.
  const signupAddOns = eventSignupAddOns(eventDetails);
  // Regeln aus der Prozessvorlage (Vorkasse, Einladungslink).
  const rules = eventRules(eventDetails);
  // Offene Anmeldungen — nur für Admins geladen.
  const [pendingCount, setPendingCount] = useState(null);
  // Bumpt sich bei jeder Abmeldung — UnregisteredPlayersSection lädt darauf neu.
  const [unregisteredRefresh, setUnregisteredRefresh] = useState(0);

  const { onlineEpoch } = useOffline();
  // Status der lokalen Kopie (nur Spieltagleiter) — speist den Status-Banner.
  const [offlineMeta, setOfflineMeta] = useState({ isLeader: false, fetchedAt: null, noCache: false });
  const [syncTick, setSyncTick] = useState(0);

  // Lädt Teilnehmerliste + Event-Details. Offline-fähig für Spieltagleiter (siehe offlineApi):
  // online frisch holen + (nur Leiter) lokal cachen; offline aus dem lokalen Cache lesen.
  // onlineEpoch: nach "Erneut verbinden" frisch vom Server laden.
  useEffect(() => {
    let cancelled = false;
    loadEvent(eventId, eventDate)
      .then((res) => {
        if (cancelled) return;
        const list = res.participants ?? [];
        setPosts(list);
        if (list.length > 0) {
          setMaxGroup(Math.max(0, ...list.map(post => post.group ?? 0)));
        }
        setEventDetails(res.details ?? null);
        setOfflineMeta({ isLeader: !!res.isLeader, fetchedAt: res.fetchedAt ?? null, noCache: !!res.noCache });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eventId, eventDate, syncTick, onlineEpoch]);

  // Nach erfolgreichem Hintergrund-Sync dieses Events neu laden (Reconcile mit Server-Wahrheit).
  useEffect(() => subscribeSync((eventIds) => {
    if (Array.isArray(eventIds) && eventIds.includes(Number(eventId))) {
      setSyncTick(t => t + 1);
    }
  }), [eventId]);

  useEffect(() => {
    if (!isAdmin()) return;
    api.get(`/Registration/ByEvent/${eventId}`)
      .then(res => {
        const pending = (res.data ?? []).filter(r => !r.isProcessed);
        setPendingCount(pending.length);
      })
      .catch(() => {});
  }, [eventId, onlineEpoch]);

  //Add Form
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const [formError, setFormError] = useState(null);

  // Form-State zusammengefasst — 7 useState aufgelöst zu einem Object.
  // Lese-Zugriffe bleiben via Destructuring identisch (vorname statt formData.vorname),
  // Set-Zugriffe gehen über setFormField(key, value).
  // addOns: { [addOnId]: Menge } der Anmelde-Zusätze.
  const EMPTY_FORM = {
    vorname: "", nachname: "", email: "", phone: "",
    addOns: {}, useWaitlist: false, isOrganizing: false,
  };
  const [formData, setFormData] = useState(EMPTY_FORM);
  const { vorname, nachname, email, phone, addOns: addOnQuantities, useWaitlist, isOrganizing } = formData;
  const setFormField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const setAddOnQuantity = (addOnId, quantity) =>
    setFormData(prev => ({ ...prev, addOns: { ...prev.addOns, [addOnId]: quantity } }));

  const handleAddNewPlayerClick = (event) => {
    event.preventDefault();
    const anyFilled = [nachname, vorname, email, phone].some(v => v && v.trim() !== "");
    if (!anyFilled) {
      setFormError("Bitte mindestens ein Feld ausfüllen.");
      return;
    }
    setFormError(null);
    addParticipant(buildBaseRequest());
  };

  // Für eine Neuanlage müssen ALLE 4 Felder gefüllt sein — sonst bleiben die
  // Stammdaten unvollständig (Mailversand/Anruf später unmöglich).
  const getMissingCreateFields = () => {
    const missing = [];
    if (!nachname || !nachname.trim()) missing.push('Nachname');
    if (!vorname || !vorname.trim()) missing.push('Vorname');
    if (!email || !email.trim()) missing.push('E-Mail');
    if (!phone || !phone.trim()) missing.push('Telefon');
    return missing;
  };

  const handlePickCandidate = (playerId) => {
    closePopup();
    const base = pendingRequest ?? buildBaseRequest();
    addParticipant({ ...base, referencePlayerId: playerId });
  };

  const handleCreateNewFromPicker = () => {
    const missing = getMissingCreateFields();
    closePopup();
    if (missing.length > 0) {
      setFormError(`Für die Neuanlage fehlen: ${missing.join(', ')}.`);
      return;
    }
    const base = pendingRequest ?? buildBaseRequest();
    addParticipant({ ...base, createNew: true });
  };

  const handleConfirmCreateNew = () => {
    const missing = getMissingCreateFields();
    if (missing.length > 0) {
      // Popup schließt sich über Popup.handleOkClick automatisch; Fehler wird auf dem Formular sichtbar.
      setFormError(`Für die Neuanlage fehlen: ${missing.join(', ')}.`);
      return;
    }
    const base = pendingRequest ?? buildBaseRequest();
    addParticipant({ ...base, createNew: true });
  };

  // Spieltag voll → normale Warteliste; Zusatz ausgebucht → Zusatz-Warteliste (eigene Mail, {Zusatz}).
  const handleConfirmAddToWaitlist = () => {
    const base = pendingRequest ?? buildBaseRequest();
    if (waitlistReason === 'addOn') addParticipant({ ...base, forceAddOnWaitlist: true });
    else addParticipant({ ...base, participantType: 'Waitlist' });
  };

  const handleSkipWaitlist = () => {
    closePopup();
    const base = pendingRequest ?? buildBaseRequest();
    addParticipant({ ...base, forceConfirm: true });
  };

  // Restore eines abgemeldeten Spielers — Admin hat im Popup bestätigt.
  // ConfirmRestore=true sagt dem Backend: weiter ohne erneute Rückfrage.
  // Spieltag voll → gleichzeitig per ParticipantType=Waitlist auf die Warteliste; Zusatz ausgebucht →
  // ForceAddOnWaitlist (Zusatz-Warteliste) — der Popup-Text hat das angekündigt.
  const handleConfirmRestoreExisting = () => {
    const base = pendingRequest ?? buildBaseRequest();
    addParticipant({
      ...base,
      confirmRestore: true,
      participantType: waitlistReason === 'event' ? 'Waitlist' : (base.participantType ?? 'Confirmed'),
      forceAddOnWaitlist: waitlistReason === 'addOn' || !!base.forceAddOnWaitlist
    });
  };

  const handleSkipWaitlistOnRestore = () => {
    closePopup();
    const base = pendingRequest ?? buildBaseRequest();
    addParticipant({ ...base, confirmRestore: true, forceConfirm: true });
  };

  const buildBaseRequest = () => ({
    lastName: nachname,
    firstName: vorname,
    email,
    phone,
    addOns: toQuantityInputs(addOnQuantities),
    group: selectedGroup,
    isOrganizing
  });

  const resetForm = () => {
    setFormError(null);
    setFormData(EMPTY_FORM);
    setSelectedGroup(0);
    setPendingRequest(null);
    setCandidates([]);
  };

  // Mappt FE-Toggles auf das ParticipantType-Enum des Backends.
  const resolveParticipantType = () => {
    if (useWaitlist) return 'Waitlist';
    return 'Confirmed';
  };

  const addParticipant = (input) => {
    startSuccessBarLoading("Spieler wird hinzugefügt");

    const requestBody = {
      eventId: Number(eventId),
      lastName: input.lastName ?? null,
      firstName: input.firstName ?? null,
      email: input.email ?? null,
      phone: input.phone ?? null,
      // Spieltagleiter bekommen keine Anmelde-Zusätze (Backend erzwingt das ebenfalls).
      addOns: input.isOrganizing ? [] : (input.addOns ?? []),
      participantType: input.participantType ?? resolveParticipantType(),
      group: input.group ?? 0,
      referencePlayerId: input.referencePlayerId ?? null,
      createNew: !!input.createNew,
      isOrganizing: !!input.isOrganizing,
      forceConfirm: !!input.forceConfirm,
      confirmRestore: !!input.confirmRestore,
      forceAddOnWaitlist: !!input.forceAddOnWaitlist
    };

    api.post('/EventPlayer/Add', requestBody)
      .then((response) => {
        const result = response.data ?? {};
        if (result.status === 'Created' && result.participant) {
          const onWaitlist = result.participant.participantType === 'Waitlist' || result.participant.participantType === 3;
          // Zusätze ohne Warteliste werden bei Knappheit gekürzt — direkt gebucht, nur gemeldet.
          startSuccessBarSuccess(withReductions(onWaitlist ? "Spieler auf Warteliste gesetzt" : "Spieler hinzugefügt", result.reducedAddOns));
          const updatedPosts = [...posts, result.participant];
          setMaxGroup(Math.max(0, ...updatedPosts.map(p => p.group ?? 0)));
          setPosts(updatedPosts);
          // Wenn ein abgemeldeter EP wiederhergestellt wurde, ist er aus der "Abgemeldet"-Liste
          // verschwunden — Section neu laden.
          setUnregisteredRefresh(k => k + 1);
          resetForm();
        } else if (result.status === 'WouldBeRestored') {
          closeSuccessBar();
          setWaitlistReason(result.waitlistReason ?? null);
          setWaitlistAddOns(result.waitlistAddOns ?? []);
          setPendingRequest(requestBody);
          openPopup('confirmrestoreexistingplayer', 1);
        } else if (result.status === 'WouldBeWaitlisted') {
          closeSuccessBar();
          setWaitlistReason(result.waitlistReason);
          setWaitlistAddOns(result.waitlistAddOns ?? []);
          setPendingRequest(requestBody);
          openPopup('confirmaddwaitlist', 1);
        } else if (result.status === 'NeedsSelection' && Array.isArray(result.candidates)) {
          closeSuccessBar();
          setCandidates(result.candidates);
          setPendingRequest(requestBody);
          openPopup('selectplayer', 1);
        } else if (result.status === 'NoMatch') {
          closeSuccessBar();
          setPendingRequest(requestBody);
          openPopup('confirmcreatenew', 1);
        } else {
          closeSuccessBar();
          setFormError("Unerwartete Antwort vom Server.");
        }
      })
      .catch(error => {
        closeSuccessBar();
        setFormError(error?.response?.data ?? "Spieler konnte nicht hinzugefügt werden");
      });
  };

  const handleIsWaitlistPlayerChange  = (e) => setFormField('useWaitlist',   e.target.checked);
  const handleIsOrganizingChange      = (e) => setFormField('isOrganizing',  e.target.checked);

  // Zweite Zeile je Zusatz im Hinzufügen-Formular: Menge je Person + Restbestand, live aus der geladenen
  // Teilnehmerliste (beide Kanäle, ohne Warteliste). Knapp/ausgebucht sagt, was beim Hinzufügen passiert.
  const addFormAddOnHint = (addOn, quantity) => {
    const parts = [];
    if (Number(addOn.maxPerPerson) > 1) parts.push(`bis ${addOn.maxPerPerson} je Person`);
    const max = Number(addOn.maxQuantity) || 0;
    if (max > 0) {
      const used = taken(posts, addOn.addOnId);
      const left = available(max, used);
      if (left >= Math.max(quantity, 1)) {
        parts.push(`noch ${left} frei`);
      } else {
        const consequence = addOn.waitlistWhenFull
          ? 'Zusatz-Warteliste'
          : left === 0 ? 'wird nicht gebucht' : `wird auf ${left} gekürzt`;
        const state = left === 0 ? `ausgebucht (${used}/${max})` : `nur noch ${left} frei`;
        return (
          <>
            {parts.length > 0 && `${parts.join(' · ')} · `}
            <span className="eventAddOnHintWarn">{state} – {consequence}</span>
          </>
        );
      }
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  return (
    <React.Fragment>
      <div className={"generalContainer"}>
        <div data-tour="event-list">
          <EventList
            posts={posts} setPosts={setPosts}
            eventId={eventId} eventDate={eventDate} addPlayer={addParticipant}
            maxGroup={maxGroup} setMaxGroup={setMaxGroup} resetCreateForm={resetForm}
            maxPlayers={eventDetails?.maxPlayers ?? 0}
            pendingCount={pendingCount}
            offlineStatus={offlineMeta}
            onUnregister={() => setUnregisteredRefresh(k => k + 1)}
            event={eventDetails}
            autoPaidOnPresence={!!eventDetails?.autoPaidOnPresence}
          />
          <UnregisteredPlayersSection
            eventId={eventId}
            event={eventDetails}
            refreshKey={unregisteredRefresh + onlineEpoch}
            onRestore={(restored) => {
              const updated = [...posts, restored];
              setMaxGroup(Math.max(0, ...updated.map(p => p.group ?? 0)));
              setPosts(updated);
            }}
          />
        </div>
        <div data-tour="event-add-form">
        <CreateForm onSubmit={handleAddNewPlayerClick} icon="person_add" error={formError} tooltip={"Spieler zum Spieltag hinzufügen"}>
          <Textfield value={vorname}  onChange={e => setFormField('vorname',  e.target.value)} type="text" label="Vorname"      placeholder="Max"                       name="firstname" />
          <Textfield value={nachname} onChange={e => setFormField('nachname', e.target.value)} type="text" label="Nachname"     placeholder="Mustermann"                name="lastname"  />
          <Textfield value={email}    onChange={e => setFormField('email',    e.target.value)} type="text" label="E-Mail"       placeholder="max.mustermann@test.de"    name="email"     />
          <Textfield value={phone}    onChange={e => setFormField('phone',    e.target.value)} type="text" label="Telefonnummer" placeholder="0711 1234567"             name="phone"     />
          {/* Anmelde-Zusätze des Prozesses — erst nach dem Laden der Spieltag-Daten (sonst Höhensprung im
              mobilen Sheet). Spieltagleiter bekommen keine: Eingaben gesperrt, solange der Schalter an ist. */}
          {signupAddOns.length > 0 && (
            <EventAddOnInputs
              label={'Zusätze'}
              addOns={signupAddOns}
              values={addOnQuantities}
              onChange={setAddOnQuantity}
              hintFor={addFormAddOnHint}
              disabled={isOrganizing}
              note={isOrganizing ? 'Spieltagleiter bekommen keine Anmelde-Zusätze.' : null}
            />
          )}
          <CustomTogglebox onChange={handleIsWaitlistPlayerChange} checked={useWaitlist} label={'Warteliste'} />
          <CustomTogglebox onChange={handleIsOrganizingChange} checked={isOrganizing} label={'Spieltagleitung'} />
          <CustomComboBox label={'Team'} selected={selectedGroup} onStatusChange={setSelectedGroup}>
            <Icon key={-1} className={`eventCreateFormGroupIcon group0`} icon={`close`} />
            {
              Array.from({ length: maxGroup + 1 }, (_, index) => (
                <div key={index} className={`groupBadge group${index + 1}`}>{index + 1}</div>
              ))
            }
          </CustomComboBox>
        </CreateForm>
        </div>
      </div>

      {/* ---------- Kandidaten-Auswahl ---------------------- */}
      {/* withConfirm={false}: Auswahl per Klick auf Kandidat ODER via "Neu anlegen"-Button. */}
      <Popup name='selectplayer' headerText='Welcher Spieler?' type="info" withCancel withConfirm={false}>
        <p>Es wurden {candidates.length === 1 ? 'ein passender Spieler' : `${candidates.length} passende Spieler`} gefunden. Bitte auswählen oder neu anlegen:</p>
        <div className={"registrationsPickList"}>
          {candidates.map((m) => {
            const mc = scoreClass(m.score);
            return (
              <button
                type="button"
                key={m.playerId}
                className={`registrationsPickItem ${mc} ${m.alreadyInEvent ? 'alreadyInEvent' : ''}`}
                onClick={() => handlePickCandidate(m.playerId)}
              >
                <span className={`registrationsPickScore ${mc}`}>{m.score}</span>
                <span className={"registrationsPickName"}>
                  <span className={"registrationsPickNameMain"}>
                    <ParticipantGroupDot row={m} />
                    {m.firstName} {m.lastName}
                  </span>
                  <CandidateGroupMeta candidate={m} price={m.priceForEvent} />
                  {m.namesSwapped && (
                    <span className={"swapCandidateHint"}>
                      <Icon className={"swapCandidateHintIcon"} icon={'swap_horiz'} />
                      Namen vertauscht?
                    </span>
                  )}
                  {m.alreadyInEvent && !m.email && (
                    <span className={"registrationsPickAlready"}>
                      <Icon className={"registrationCardChipIcon warn"} icon={'warning'} />
                      bereits im Spieltag
                    </span>
                  )}
                </span>
                <span className={"registrationsPickContact"}>
                  {m.alreadyInEvent && m.email && (
                    <span className={"registrationsPickAlready"}>
                      <Icon className={"registrationCardChipIcon warn"} icon={'warning'} />
                      bereits im Spieltag
                    </span>
                  )}
                  <span>{m.email || '–'}{m.phone ? ` · ${m.phone}` : ''}</span>
                </span>
              </button>
            );
          })}
          <button
            type="button"
            className={"eventPickCreateNew"}
            onClick={handleCreateNewFromPicker}
          >
            <Icon className={"registrationCardChipIcon"} icon={'person_add'} />
            Als neuen Spieler anlegen
          </button>
        </div>
      </Popup>

      {/* ---------- Warteliste-Hinweis ----------------------- */}
      <Popup
        name='confirmaddwaitlist'
        headerText='Spieler auf Warteliste setzen?'
        type="warning"
        withCancel
        withConfirm
        confirmText='Zur Warteliste hinzufügen'
        onConfirm={handleConfirmAddToWaitlist}
      >
        {waitlistReason === 'addOn' ? (
          <p>
            {describeExhaustedAddOns(waitlistAddOns)}.
            <br />Der Spieler wird auf die <b>Zusatz-Warteliste</b> gesetzt und bekommt die Mail „{mailSlotLabel('equipment_waitlist')}“, sofern sie für seine Gruppe eingeschaltet ist.
          </p>
        ) : (
          <p>
            Der Spieltag ist ausgebucht
            {eventDetails?.maxPlayers > 0 ? ` (${eventDetails.maxPlayers}/${eventDetails.maxPlayers} Plätze belegt)` : ''}.
            <br />Der Spieler wird auf die <b>Warteliste</b> gesetzt und bekommt die Mail „{mailSlotLabel('waitlist')}“, sofern sie für seine Gruppe eingeschaltet ist.
          </p>
        )}
        <button type="button" className="registrationsForceConfirmBtn" onClick={handleSkipWaitlist}>
          <Icon className="registrationsForceConfirmIcon" icon="bolt" />
          {waitlistReason === 'addOn' ? 'Trotzdem hinzufügen (Zusatz-Limit überschreiten)' : 'Trotzdem hinzufügen (Warteliste überspringen)'}
        </button>
      </Popup>

      {/* ---------- Wiederherstellen eines abgemeldeten Spielers ---------- */}
      <Popup
        name='confirmrestoreexistingplayer'
        headerText='Abgemeldeten Spieler wiederherstellen?'
        type="info"
        withCancel
        withConfirm
        confirmText={waitlistReason ? 'Auf Warteliste setzen' : 'Wiederherstellen'}
        onConfirm={handleConfirmRestoreExisting}
      >
        {waitlistReason === 'addOn' ? (
          <p>
            Der Spieler war bereits abgemeldet und wird wiederhergestellt — alter Mail-Verlauf
            bleibt verlinkt.
            <br />{describeExhaustedAddOns(waitlistAddOns)} — er wird auf die <b>Zusatz-Warteliste</b> gesetzt und bekommt die Mail „{mailSlotLabel('equipment_waitlist')}“, sofern sie für seine Gruppe eingeschaltet ist.
          </p>
        ) : waitlistReason === 'event' ? (
          <p>
            Der Spieler war bereits abgemeldet und wird wiederhergestellt — alter Mail-Verlauf
            bleibt verlinkt.
            <br />Der Spieltag ist ausgebucht{eventDetails?.maxPlayers > 0 ? ` (${eventDetails.maxPlayers}/${eventDetails.maxPlayers} Plätze belegt)` : ''} — er wird auf die <b>Warteliste</b> gesetzt und bekommt die Mail „{mailSlotLabel('waitlist')}“, sofern sie für seine Gruppe eingeschaltet ist.
          </p>
        ) : (
          <p>
            Der Spieler war bereits abgemeldet und wird wiederhergestellt. Alter Mail-Verlauf
            und alle verknüpften Daten bleiben am Eintrag verlinkt.{' '}
            {rules.prepaymentRequired
              ? <>Bei Vorkasse bekommt ein zahlungspflichtiger Spieler die Mail „{mailSlotLabel('payment_request')}“ — die Bestätigung verschickst du mit „Als bezahlt markieren“.</>
              : 'Er bekommt die übliche Statusmail, sofern sie für seine Gruppe eingeschaltet ist.'}
          </p>
        )}
        {waitlistReason && (
          <button type="button" className="registrationsForceConfirmBtn" onClick={handleSkipWaitlistOnRestore}>
            <Icon className="registrationsForceConfirmIcon" icon="bolt" />
            {waitlistReason === 'addOn' ? 'Trotzdem hinzufügen (Zusatz-Limit überschreiten)' : 'Trotzdem hinzufügen (Warteliste überspringen)'}
          </button>
        )}
      </Popup>

      {/* ---------- Keine Treffer → Neuanlage bestätigen ---- */}
      {(() => {
        const missing = getMissingCreateFields();
        return (
          <Popup name='confirmcreatenew'
            headerText={missing.length > 0 ? 'Neuanlage unvollständig' : 'Spieler neu anlegen?'}
            type={missing.length > 0 ? 'warning' : 'info'}
            withCancel confirmText='Anlegen' onConfirm={handleConfirmCreateNew}>
            <p>Spieler noch nicht in der DB und wird neu erstellt.</p>
            {missing.length > 0 && (
              <p>Für die Neuanlage fehlen noch: <b>{missing.join(', ')}</b>. Bitte Formular ergänzen.</p>
            )}
          </Popup>
        );
      })()}
    </React.Fragment>
  );
}

export default Event;
